import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useFunnel } from '../context/FunnelContext'
import { getFunnelIdFromPath } from '../utils/funnelUtils'
import { sessionStore } from '../utils/sessionKey'
import { trackTikTokInitiateCheckout } from '../utils/tiktokTracking'
import { trackGtmAddToCart } from '../utils/googleAnalytics'
import { logger } from '../utils/logger'

const SESSION_KEY_PREFIX = 'tiktok_initiate_checkout_tracked_'

const isCheckoutPaymentPath = (pathname = '') => pathname.includes('/checkout/payment')

const getSelectedProduct = (funnelData = {}) =>
  funnelData.selected_product_medication ||
  funnelData.selected_product_id ||
  funnelData.selected_product_name ||
  ''

const getSelectedPlan = (funnelData = {}) => funnelData.selected_plan || ''

/**
 * Fire TikTok InitiateCheckout when the user lands on
 * /{funnel}/checkout/payment/:caseId (cv-1, cv-2, cv-3, nad, sermorelin).
 *
 * Deduped per funnel + product + plan, so going back to the selection step and
 * picking a different product or plan fires a new event, while a refresh or a
 * re-entry with the same selection stays deduped for the session.
 */
export const useTikTokInitiateCheckoutTracking = () => {
  const location = useLocation()
  const { funnelData, isInitialized } = useFunnel()

  const selectedProduct = getSelectedProduct(funnelData)
  const selectedPlan = getSelectedPlan(funnelData)

  useEffect(() => {
    if (!isCheckoutPaymentPath(location.pathname)) return

    // funnelData hydrates asynchronously (localStorage, or backend on cross-device
    // entry); firing before that would send an event with no product context.
    if (!isInitialized) return
    if (!selectedProduct && !selectedPlan) {
      logger.debug('[TikTok] InitiateCheckout waiting for product/plan selection')
      return
    }

    const funnelId =
      funnelData?.funnel_id || getFunnelIdFromPath(location.pathname) || 'unknown'
    const selectionKey = `${selectedProduct || 'no-product'}_${selectedPlan || 'no-plan'}`
    const sessionKey = `${SESSION_KEY_PREFIX}${funnelId}_${selectionKey}`

    if (sessionStore.getItem(sessionKey) === 'true') {
      logger.debug('[TikTok] InitiateCheckout already tracked this session', {
        funnelId,
        selectionKey,
      })
      return
    }

    sessionStore.setItem(sessionKey, 'true')
    trackTikTokInitiateCheckout({ ...funnelData, funnel_id: funnelId })
    trackGtmAddToCart({ ...funnelData, funnel_id: funnelId })
  }, [location.pathname, isInitialized, selectedProduct, selectedPlan])
}

export default useTikTokInitiateCheckoutTracking
